import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Logo } from "@/components/site/Layout";
import { useSeo } from "@/context/ContentContext";
import { useAuth, homeFor } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

const SIDE_IMG = "https://images.unsplash.com/photo-1758448756350-3d0eec02ba37?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const DISCIPLINES = [
  ["physiotherapy", "Physiotherapy"], ["massage-therapy", "Massage Therapy"], ["chiropractic", "Chiropractic"],
  ["counselling", "Counselling"], ["acupuncture", "Acupuncture"], ["osteopathy", "Osteopathy"],
];

const Shell = ({ title, sub, children }) => (
  <div className="min-h-screen grid lg:grid-cols-2">
    <div className="flex flex-col px-6 sm:px-12 py-8">
      <Logo />
      <div className="flex-1 flex items-center">
        <div className="w-full max-w-md mx-auto py-10 fade-up">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">{title}</h1>
          <p className="text-muted-foreground mt-2 mb-8">{sub}</p>
          {children}
        </div>
      </div>
    </div>
    <div className="hidden lg:block relative">
      <img src={SIDE_IMG} alt="Clinic treatment room" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-[#1E3A34]/30" />
    </div>
  </div>
);

const Field = ({ id, label, ...props }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} className="h-11 bg-card" data-testid={`${id}-input`} {...props} />
  </div>
);

const useSubmit = (fn) => {
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const [params] = useSearchParams();
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await fn();
      const next = params.get("next");
      nav(next && next.startsWith("/") && !next.startsWith("//") ? next : homeFor(u));
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally { setBusy(false); }
  };
  return { err, busy, submit, params };
};

export function Login() {
  useSeo("login");
  const { login } = useAuth();
  const [f, setF] = useState({ email: "", password: "" });
  const { err, busy, submit, params } = useSubmit(() => login(f.email, f.password));
  const q = params.toString() ? `?${params.toString()}` : "";
  return (
    <Shell title="Welcome back" sub="Log in to manage your bookings or your clinic.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field id="login-email" label="Email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Field id="login-password" label="Password" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        {err && <p className="text-sm text-rose-600" data-testid="login-error">{err}</p>}
        <Button type="submit" className="w-full h-11 rounded-full" disabled={busy} data-testid="login-form-submit-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log in"}</Button>
        <p className="text-sm text-muted-foreground text-center">New here? <Link to={`/signup${q}`} className="text-primary font-medium" data-testid="login-to-signup-link">Create a patient account</Link></p>
      </form>
    </Shell>
  );
}

export function Signup() {
  useSeo("signup");
  const { register } = useAuth();
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "" });
  const { err, busy, submit, params } = useSubmit(() => register(f));
  const q = params.toString() ? `?${params.toString()}` : "";
  return (
    <Shell title="Create your account" sub="Book appointments and keep track of your visits.">
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        <Field id="signup-name" label="Full name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <Field id="signup-email" label="Email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Field id="signup-phone" label="Phone (optional)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
        <Field id="signup-password" label="Password" type="password" minLength={6} required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        {err && <p className="text-sm text-rose-600" data-testid="signup-error">{err}</p>}
        <Button type="submit" className="w-full h-11 rounded-full" disabled={busy} data-testid="signup-form-submit-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}</Button>
        <p className="text-sm text-muted-foreground text-center">Have an account? <Link to={`/login${q}`} className="text-primary font-medium" data-testid="signup-to-login-link">Log in</Link></p>
      </form>
    </Shell>
  );
}

export function ClinicSignup() {
  useSeo("for_clinics");
  const { register } = useAuth();
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "", clinic_name: "", discipline: "physiotherapy", city: "" });
  const { err, busy, submit } = useSubmit(() => register(f, true));
  return (
    <Shell title="List your clinic" sub="Set up your booking page. We review new clinics before they go live.">
      <form onSubmit={submit} className="space-y-4" data-testid="clinic-signup-form">
        <Field id="clinic-signup-clinic-name" label="Clinic name" required value={f.clinic_name} onChange={(e) => setF({ ...f, clinic_name: e.target.value })} />
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Main treatment</Label>
            <Select value={f.discipline} onValueChange={(v) => setF({ ...f, discipline: v })}>
              <SelectTrigger className="h-11 bg-card" data-testid="clinic-signup-discipline-select"><SelectValue /></SelectTrigger>
              <SelectContent>{DISCIPLINES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Field id="clinic-signup-city" label="City" required value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} />
        </div>
        <Field id="clinic-signup-name" label="Your name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <Field id="clinic-signup-email" label="Work email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Field id="clinic-signup-password" label="Password" type="password" minLength={6} required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        {err && <p className="text-sm text-rose-600" data-testid="clinic-signup-error">{err}</p>}
        <Button type="submit" className="w-full h-11 rounded-full" disabled={busy} data-testid="clinic-signup-submit-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create clinic account"}</Button>
      </form>
    </Shell>
  );
}
